import { ref, computed, watch, type ComputedRef } from 'vue'

// =============================================================================
// Types
// =============================================================================

type Theme = 'light' | 'dark' | 'auto'

interface UseThemeReturn {
    theme: ComputedRef<Theme>
    systemTheme: ComputedRef<'light' | 'dark'>
    resolvedTheme: ComputedRef<'light' | 'dark'>
    isDarkMode: ComputedRef<boolean>
    isLightMode: ComputedRef<boolean>
    isAutoMode: ComputedRef<boolean>
    setTheme: (newTheme: Theme) => void
    toggleTheme: () => void
    initializeTheme: () => () => void
}

// =============================================================================
// Shared State (Singleton)
// =============================================================================

// Light by default, matching the CRM template.
const theme = ref<Theme>((localStorage.getItem('theme') as Theme) || 'light')
const systemTheme = ref<'light' | 'dark'>(
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
)

// =============================================================================
// useTheme Composable
// =============================================================================

export function useTheme(): UseThemeReturn {
    const resolvedTheme = computed<'light' | 'dark'>(() =>
        theme.value === 'auto' ? systemTheme.value : theme.value
    )
    const isDarkMode = computed(() => resolvedTheme.value === 'dark')
    const isLightMode = computed(() => resolvedTheme.value === 'light')
    const isAutoMode = computed(() => theme.value === 'auto')

    const applyTheme = (newTheme: Theme): void => {
        const resolved = newTheme === 'auto' ? systemTheme.value : newTheme

        if (resolved === 'dark') {
            document.documentElement.classList.add('dark')
        } else {
            document.documentElement.classList.remove('dark')
        }
    }

    const setTheme = (newTheme: Theme): void => {
        theme.value = newTheme
        localStorage.setItem('theme', newTheme)
        applyTheme(newTheme)
    }

    const toggleTheme = (): void => {
        const newTheme = resolvedTheme.value === 'light' ? 'dark' : 'light'
        setTheme(newTheme)
    }

    const initializeTheme = (): (() => void) => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const handler = (e: MediaQueryListEvent): void => {
            systemTheme.value = e.matches ? 'dark' : 'light'
            if (theme.value === 'auto') {
                applyTheme('auto')
            }
        }

        mediaQuery.addEventListener('change', handler)
        applyTheme(theme.value)

        return () => mediaQuery.removeEventListener('change', handler)
    }

    watch(
        () => theme.value,
        (newTheme) => {
            applyTheme(newTheme)
        }
    )

    return {
        // State
        theme: computed(() => theme.value),
        systemTheme: computed(() => systemTheme.value),
        resolvedTheme,
        isDarkMode,
        isLightMode,
        isAutoMode,

        // Actions
        setTheme,
        toggleTheme,
        initializeTheme
    }
}
